import { collection, DocumentData, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";

const PHARMETIKA_PENDING_ORDERS_COLLECTION = "pharmetika_pending_orders";

export interface FlattenedPharmetikaOrders {
  pendingOrders: any[];
  archivedOrders: any[];
}

let cachedOrders: FlattenedPharmetikaOrders | null = null;
let pendingOrdersRequest: Promise<FlattenedPharmetikaOrders> | null = null;

const sortByOrderDateDesc = (a: any, b: any) => {
  const dateA = a.orderDate ? new Date(a.orderDate).getTime() : 0;
  const dateB = b.orderDate ? new Date(b.orderDate).getTime() : 0;
  return dateB - dateA;
};

const cloneOrders = (orders: FlattenedPharmetikaOrders): FlattenedPharmetikaOrders => ({
  pendingOrders: orders.pendingOrders.map((order) => ({ ...order })),
  archivedOrders: orders.archivedOrders.map((order) => ({ ...order })),
});

export const invalidatePharmetikaPendingOrdersCache = () => {
  cachedOrders = null;
  pendingOrdersRequest = null;
};

export const getPharmetikaPendingOrders = async (
  forceRefresh = false
): Promise<FlattenedPharmetikaOrders> => {
  if (forceRefresh) {
    invalidatePharmetikaPendingOrdersCache();
  }

  if (cachedOrders) {
    return cloneOrders(cachedOrders);
  }

  if (!pendingOrdersRequest) {
    pendingOrdersRequest = (async () => {
      const pendingOrdersQuery = query(
        collection(db, PHARMETIKA_PENDING_ORDERS_COLLECTION),
        where("status", "==", "pending_review"),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(pendingOrdersQuery);
      const pendingOrders: any[] = [];
      const archivedOrders: any[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() as DocumentData;
        if (!data.orders || !Array.isArray(data.orders)) {
          return;
        }

        data.orders.forEach((order: any) => {
          const flattenedOrder = {
            ...order,
            pendingOrdersId: doc.id,
            webhookId: data.webhookId,
          };

          if (order.archived) {
            archivedOrders.push(flattenedOrder);
          } else {
            pendingOrders.push(flattenedOrder);
          }
        });
      });

      pendingOrders.sort(sortByOrderDateDesc);
      archivedOrders.sort(sortByOrderDateDesc);

      cachedOrders = { pendingOrders, archivedOrders };
      pendingOrdersRequest = null;
      return cloneOrders(cachedOrders);
    })().catch((error) => {
      pendingOrdersRequest = null;
      throw error;
    });
  }

  return pendingOrdersRequest.then(cloneOrders);
};

export const getPendingPharmetikaOrdersCount = async () => {
  const { pendingOrders } = await getPharmetikaPendingOrders();
  return pendingOrders.length;
};
